import { useQuery } from "@apollo/client/react";
import { GET_DEPOSITOS_BY_ID_EVALUACION } from "../../../data/graphQL/queries/GestionAsistencial/getDepositosByIdEvaluacion";
import { adaptOptionsFromEntity } from "../../../adapters/adaptOptionsFromEntity";
import { GET_ENVIO_ESTADOS } from "../../../data/graphQL/queries/GestionAsistencial/getEnvioEstados";
import { GET_JUSTIFICACIONES_ESTADOS } from "../../../data/graphQL/queries/GestionAsistencial/getJustificacionesEstados";
import { GET_JUSTIFICACIONES_MOTIVOS } from "../../../data/graphQL/queries/GestionAsistencial/getJustificacionesMotivos";

export const ENVIO_ESTADO_AUTORIZAR_ID = 1;
export const ENVIO_ESTADO_AUTORIZADO_ID = 2;
export const ENVIO_ESTADO_CANCELADO_ID = 3;
export const ENVIO_ESTADO_PENDIENTE_ID = 4;

export const ESTADO_JUSTIFICACION_APROBADO_ID = 2;

export function useEnvioCreationContentInputs({
  idEvaluacion,
  isNew,
  estadoValue,
  allowedWrite,
  disabledNormalInput,
  handleOnBlurFecha,
  enterFromScreen,
  tieneSuspensionObito
}) {
  const { data: estados } = useQuery(GET_ENVIO_ESTADOS);

  const { data: justificacionEstados } = useQuery(GET_JUSTIFICACIONES_ESTADOS, {
    skip: isNew
  });

  const { data: justificacionMotivos } = useQuery(GET_JUSTIFICACIONES_MOTIVOS, {
    skip: isNew
  });

  const { data: depositos } = useQuery(GET_DEPOSITOS_BY_ID_EVALUACION, {
    variables: {
      idEvaluacion
    }
  });

  const depositoOptions = adaptOptionsFromEntity(
    depositos,
    "depositosByIdEvaluacion",
    {
      idKey: "idDeposito",
      displayNameKey: "deposito"
    }
  );

  const estadosOptions = adaptOptionsFromEntity(estados, "envioEstados", {
    idKey: "idEstado",
    displayNameKey: "descripcion"
  }).filter(({ displayName }) => {
    if (tieneSuspensionObito && displayName === "Autorizado") return false;

    if (isNew) return ["Pendiente", "A Autorizar"].includes(displayName);

    const opcionesPorEstado = {
      [ENVIO_ESTADO_PENDIENTE_ID]: ["Pendiente", "A Autorizar", "Cancelado"],
      [ENVIO_ESTADO_AUTORIZAR_ID]: ["Pendiente", "A Autorizar", "Autorizado", "Cancelado"],
      [ENVIO_ESTADO_AUTORIZADO_ID]: ["Autorizado", "Cancelado"]
    };

    if (!enterFromScreen) {
      opcionesPorEstado[ENVIO_ESTADO_AUTORIZAR_ID] = opcionesPorEstado[ENVIO_ESTADO_AUTORIZAR_ID].filter((estado) => estado !== "Autorizado");
    }

    return opcionesPorEstado[estadoValue]?.includes(displayName) ?? true;
  });

  const justificacionEstadosOptions = adaptOptionsFromEntity(
    justificacionEstados,
    "justicacionesEstados",
    {
      idKey: "idEstado",
      displayNameKey: "descripcion"
    }
  );

  const justificacionMotivosOptions = adaptOptionsFromEntity(
    justificacionMotivos,
    "justicacionesMotivos",
    {
      idKey: "idMotivoJustificacion",
      displayNameKey: "descripcionCorta"
    }
  );

  const inputsEnvio = [
  {
    displayName: "Estado",
    name: "idEstadoEnvio",
    type: "select",
    options: estadosOptions,
    disabled: !allowedWrite,
    col: 20
  },
  {
    displayName: "F. de envío",
    name: "fechaEntrega",
    type: "date",
    disabled: disabledNormalInput,
    col: 20
  },
  {
    displayName: "Pedido de depósito",
    name: "idDeposito",
    type: "select",
    disabled: disabledNormalInput,
    options: depositoOptions,
    col: 30
  },
  {
    displayName: "Pedido para los próx. días",
    name: "diasPedido",
    type: "int",
    disabled: disabledNormalInput,
    col: 30
  }];


  const inputsJustificacionCondicional = [
  {
    name: "Justificación Comercial",
    rows: [
    {
      displayName: "Estado",
      name: "idEstadoComercial",
      options: justificacionEstadosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Justificación",
      name: "idMotivoJustificacionComercial",
      options: justificacionMotivosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Usuario",
      name: "usuarioComercial",
      disabled: true,
      type: "string",
      col: 28
    },
    {
      displayName: "Fecha",
      name: "fechaComercial",
      disabled: true,
      type: "date",
      col: 16
    },
    {
      displayName: "Observación",
      name: "obsComercial",
      disabled: true,
      type: "long_string",
      col: 100
    }]

  },
  {
    name: "Justificación Médico",
    rows: [
    // First row
    {
      displayName: "Estado",
      name: "idEstadoMedico",
      options: justificacionEstadosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Justificación",
      name: "idMotivoJustificacionMedico",
      options: justificacionMotivosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Usuario",
      name: "usuarioMedico",
      type: "string",
      disabled: true,
      col: 28
    },
    {
      displayName: "Fecha",
      name: "fechaMedico",
      type: "date",
      disabled: true,
      col: 16
    },
    // Second row
    {
      displayName: "Observación",
      name: "obsMedico",
      disabled: true,
      type: "long_string",
      col: 100
    }]

  },
  {
    name: "Justificación BUM",
    rows: [
    // First row
    {
      displayName: "Estado",
      name: "idEstadoBUM",
      options: justificacionEstadosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Justificación",
      name: "idMotivoJustificacionBUM",
      options: justificacionMotivosOptions,
      type: "select",
      disabled: true,
      col: 28
    },
    {
      displayName: "Usuario",
      name: "usuarioBUM",
      type: "string",
      disabled: true,
      col: 28
    },
    {
      displayName: "Fecha",
      name: "fechaBUM",
      type: "date",
      disabled: true,
      col: 16
    },
    // Second row
    {
      displayName: "Observación",
      name: "obsBUM",
      disabled: true,
      type: "long_string",
      col: 100
    }]

  }];


  const inputsDireccion = [
  // First row
  {
    displayName: "Dirección",
    name: "direccion",
    type: "string",
    disabled: true,
    col: 25
  },
  {
    displayName: "Provincia",
    name: "provincia",
    type: "string",
    disabled: true,
    col: 25
  },
  {
    displayName: "Localidad",
    name: "localidad",
    type: "string",
    disabled: true,
    col: 25
  },
  {
    displayName: "Cod. Postal",
    name: "codigoPostal",
    type: "string",
    disabled: true,
    col: 15
  },
  // Second row
  {
    displayName: "País",
    name: "pais",
    disabled: true,
    type: "string",
    col: 15
  },
  {
    displayName: "Teléfono",
    name: "telefono",
    type: "string",
    disabled: true,
    col: 15
  },
  // Third row
  {
    displayName: "Observación",
    name: "observaciones",
    type: "long_string",
    disabled: disabledNormalInput,
    col: 100
  }];


  const inputsDetalleIndicacion = [
  {
    displayName: "F. desde",
    name: "fechaDesde",
    type: "date",
    disabled: true,
    col: 25
  },
  {
    displayName: "F. hasta",
    name: "fechaHasta",
    type: "date",
    disabled: true,
    col: 25
  },
  {
    displayName: "Usuario",
    name: "usuario",
    type: "string",
    disabled: true,
    col: 25
  },
  {
    displayName: "F. alta",
    name: "fechaAlta",
    type: "date",
    disabled: true,
    col: 25
  }];


  const inputsDetalleIndicacionSolicitud = [

  {
    displayName: "F. desde",
    name: "fechaDesdeSolicitud",
    type: "date",
    disabled: true,
    col: 25
  },
  {
    displayName: "F. hasta",
    name: "fechaHastaSolicitud",
    type: "date",
    disabled: true,
    col: 25
  },
  {
    displayName: "Solicitante",
    name: "solicitanteSolicitud",
    type: "string",
    disabled: true,
    col: 25
  },
  {
    displayName: "F. alta",
    name: "fechaAltaSolicitud",
    type: "date",
    disabled: true,
    col: 25
  }];


  return {
    inputsEnvio,
    inputsJustificacionCondicional,
    inputsDireccion,
    inputsDetalleIndicacion,
    inputsDetalleIndicacionSolicitud
  };
}